/** @jsxImportSource @emotion/react */

import { useLazyQuery, useMutation } from '@apollo/client'
import { Modal } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { FC, useCallback, useEffect, useState } from 'react'
import InsertLinkIcon from '@mui/icons-material/InsertLink'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { bulkRemoveStudyNote, bulkRemoveStudyNoteVariables } from '__generatedTypes__/bulkRemoveStudyNote'
import { bulkUpdateStudyNote, bulkUpdateStudyNoteVariables } from '__generatedTypes__/bulkUpdateStudyNote'
import {
  findAllStudyNote,
  findAllStudyNote_findAllStudyNote_items as IStudyNotes,
} from '__generatedTypes__/findAllStudyNote'
import { findAllStudyNoteGroup_findAllStudyNoteGroup_items as TGroup } from '__generatedTypes__/findAllStudyNoteGroup'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Badge from 'components/Badge'
import DeleteModal from 'components/Modals/DeleteModal'
import Table, { TableProps } from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import client from 'configs/apolloClient'
import { BULK_REMOVE_STUDY_NOTE, BULK_UPDATE_STUDY_NOTE, FIND_ALL_STUDY_NOTES } from 'queries/studyNote'
import { toast } from 'utils/toast'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import ManageStudyNoteModal from './ManageStudyNoteModal'
import * as classes from './styles'

interface IProps {
  groups: TGroup[]
  initialSelected: TGroup
  selectedSubjectId: string
}

const StudyNoteTabs: FC<IProps> = ({ groups, selectedSubjectId, initialSelected }) => {
  const [getStudyNotes, { data: studyNotesData }] = useLazyQuery<findAllStudyNote>(FIND_ALL_STUDY_NOTES)
  const [studyNotes, setStudyNotes] = useState<IStudyNotes[]>([])
  const [selectedGroup, setSelectedGroup] = useState<TGroup>(initialSelected)
  const [isCardModalOpen, setCardModalOpen] = useState<boolean>(false)
  const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false)
  const [selectedStudyNote, setSelectedStudyNote] = useState<IStudyNotes | null>(null)
  const [noteIdsToDelete, setNoteIdsToDelete] = useState<IStudyNotes[]>([])
  const [bulkUpdateStudyNote] = useMutation<bulkUpdateStudyNote, bulkUpdateStudyNoteVariables>(BULK_UPDATE_STUDY_NOTE)
  const [bulkRemoveStudyNote] = useMutation<bulkRemoveStudyNote, bulkRemoveStudyNoteVariables>(BULK_REMOVE_STUDY_NOTE)

  useEffect(() => {
    setNoteIdsToDelete([])
    selectedGroup &&
      getStudyNotes({
        variables: {
          query: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              studyNoteGroup: {
                id: selectedGroup.id,
              },
            },
          },
        },
      })
  }, [selectedGroup])

  useEffect(() => {
    studyNotesData && setStudyNotes(studyNotesData.findAllStudyNote.items || [])
  }, [studyNotesData])

  useEffect(() => {
    setStudyNotes([])
  }, [selectedSubjectId])

  useEffect(() => {
    if (selectedGroup && !groups.find((group) => group.id === selectedGroup.id)) {
      setSelectedGroup(groups[0])
      setStudyNotes([])
    }
  }, [groups])

  const handleModalClose = () => {
    setSelectedStudyNote(null)
    setCardModalOpen(false)
  }

  const handleEditClick = (studyNote: IStudyNotes) => {
    setSelectedStudyNote(studyNote)
    setCardModalOpen(true)
    setNoteIdsToDelete([])
  }

  const handleBulkDelete = useCallback(async () => {
    try {
      const ids = noteIdsToDelete.map((group) => ({ id: group.id }))
      await bulkRemoveStudyNote({ variables: { ids } })
      setNoteIdsToDelete([])
      setBulkDeleteModalOpen(false)
      client.refetchQueries({
        include: [FIND_ALL_STUDY_NOTES],
      })
      toast.success('Cards deleted successfully')
    } catch (error) {
      toast.error('Failed to delete cards')
    }
  }, [noteIdsToDelete])

  const StudyNotesColumns: TableProps<IStudyNotes>['columns'] = [
    {
      title: 'Title',
      renderCell: (note) => (
        <Typography css={commonClasses.longText} variant="body2" data-testid="card-title">
          {note.name}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    {
      title: 'Description',
      renderCell: (note) => (
        <Typography css={commonClasses.longText} variant="body2">
          {note.description}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    {
      title: 'Group',
      renderCell: (note) => (
        <Badge text={note.studyNoteGroup.title} color={note.studyNoteGroup.color} css={classes.badge} />
      ),
      flex: '0 1 250px',
    },
    {
      title: 'URL',
      renderCell: (note) => (
        <IconButton size="small" href={note.link} target="_blank" css={classes.link}>
          <InsertLinkIcon />
        </IconButton>
      ),
      flex: '0 1 100px',
    },
    {
      renderCell: (note) => (
        <EditIcon css={commonClasses.editIcon} onClick={() => handleEditClick(note)} data-testid="edit-card-btn" />
      ),
      flex: '0 1 29px',
    },
  ]

  const onSort = useCallback(async (items: IStudyNotes[]) => {
    try {
      if (items.length > 0) {
        setStudyNotes(items)

        await bulkUpdateStudyNote({
          variables: {
            bulkUpdateStudyNoteInput: items.map(({ id }, order) => ({ id, order })),
          },
        })
        toast.success('Cards order updated successfully')
      }
    } catch (error) {
      toast.error('Failed to update cards order')
    }
  }, [])

  return (
    <div>
      <Typography css={classes.title} variant="title3" component="p">
        Cards
      </Typography>
      <div css={classes.tabsBlock}>
        {groups.map((group) => (
          <Typography
            onClick={() => setSelectedGroup(group)}
            key={group.id}
            css={[classes.tab, selectedGroup?.id === group.id && classes.activeTab]}
            variant="body2"
          >
            {group.title}
          </Typography>
        ))}
      </div>
      <Table
        columns={StudyNotesColumns}
        items={studyNotes}
        isSelectable
        selectedItems={noteIdsToDelete}
        onSelect={setNoteIdsToDelete}
        onSort={onSort}
        isSortable
      />
      {selectedGroup && (
        <TableButtons
          itemName="Card"
          areItemsToDelete={noteIdsToDelete.length !== 0}
          onAddItem={() => setCardModalOpen(true)}
          onCancel={() => setNoteIdsToDelete([])}
          onBatchDelete={() => setBulkDeleteModalOpen(true)}
        />
      )}
      {selectedGroup !== null && (
        <Modal open={isCardModalOpen} onClose={handleModalClose}>
          <ManageStudyNoteModal
            onClose={handleModalClose}
            studyNoteGroups={groups}
            studyNote={selectedStudyNote ?? { id: 'id', studyNoteGroup: { id: selectedGroup.id } }}
            studyNotesLength={studyNotes.length}
            isEditMode={!!selectedStudyNote}
          />
        </Modal>
      )}
      <DeleteModal
        open={isBulkDeleteModalOpen}
        count={noteIdsToDelete.length}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  )
}

export default StudyNoteTabs
